import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import type { DisplayView, SiteAlertState } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { EvacuationScreen } from './EvacuationScreen';
import { useDisplayLive } from './live';
import { DisplayRevoked, fetchView } from './session';
import type { DisplaySession } from './session';

const HEX = /^#[0-9a-fA-F]{6}$/;
/** Safety net if a live message is missed; the clock and welcome list also move with time. */
const POLL_MS = 60_000;
const RETRY_MS = 10_000;
const NOTICE_MS = 12_000;
const WELCOME_MAX = 8;
export const VIEW_KEY = ['display', 'view'] as const;

const colour = (v: string, fallback: string): string => (HEX.test(v) ? v : fallback);

function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function Clock({ timeZone }: { timeZone: string }) {
  const now = useNow();
  let time: string;
  let date: string;
  try {
    time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone }).format(now);
    date = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone }).format(now);
  } catch {
    time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(now);
    date = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).format(now);
  }
  return (
    <div className="text-right" role="timer" aria-label={t('displayUi.view.clock')}>
      <p className="text-[clamp(2.5rem,7vmin,7rem)] font-bold leading-none tabular-nums">{time}</p>
      <p className="mt-[0.6vmin] text-[clamp(1rem,2.2vmin,2.5rem)] opacity-80">{date}</p>
    </div>
  );
}

const PANEL = 'rounded-[2vmin] bg-white/10 p-[2.5vmin]';
const PANEL_TITLE = 'mb-[1.5vmin] text-[clamp(1.1rem,2.2vmin,2.4rem)] font-semibold uppercase tracking-wide opacity-80';

function Welcome({ view }: { view: DisplayView }) {
  const shown = view.welcome.slice(0, WELCOME_MAX);
  const more = view.welcome.length - shown.length;
  const line = (w: DisplayView['welcome'][number]): string =>
    view.content.names === 'none' || !w.firstName
      ? t('displayUi.welcome.title')
      : w.company && view.content.names === 'first_name_company'
        ? t('displayUi.welcome.personCompany', { name: w.firstName, company: w.company })
        : t('displayUi.welcome.person', { name: w.firstName });
  return (
    <section className={`${PANEL} min-h-0`} aria-labelledby="d-welcome">
      <h2 id="d-welcome" className={PANEL_TITLE}>
        {t('displayUi.welcome.title')}
      </h2>
      {shown.length === 0 ? (
        <p className="text-[clamp(1.5rem,3.4vmin,3.5rem)] opacity-80">{t('displayUi.welcome.none')}</p>
      ) : (
        <ul className="space-y-[1.6vmin]">
          {shown.map((w, i) => (
            <li key={`${w.expectedAt}-${i}`} className="text-[clamp(1.75rem,5vmin,5.5rem)] font-semibold leading-tight">
              {line(w)}
            </li>
          ))}
          {more > 0 ? <li className="text-[clamp(1.25rem,2.8vmin,3rem)] opacity-80">{t('displayUi.welcome.more', { count: more })}</li> : null}
        </ul>
      )}
    </section>
  );
}

function Headcount({ view }: { view: DisplayView }) {
  const n = view.headcount;
  if (n === null) return null;
  const cap = view.content.capacity ? view.capacity : null;
  const pct = cap && cap.capacity > 0 ? Math.min(100, Math.round((n / cap.capacity) * 100)) : 0;
  const bar = cap?.level === 'red' ? 'bg-red-500' : cap?.level === 'amber' ? 'bg-amber-400' : 'bg-green-500';
  return (
    <section className={PANEL} aria-labelledby="d-head">
      <h2 id="d-head" className={PANEL_TITLE}>
        {t('displayUi.headcount.title')}
      </h2>
      <p className="text-[clamp(2.5rem,8vmin,8rem)] font-bold leading-none tabular-nums">{n === 1 ? t('displayUi.headcount.one') : t('displayUi.headcount.value', { count: n })}</p>
      {cap && cap.level !== 'none' && cap.capacity > 0 ? (
        <div className="mt-[2vmin]">
          <div
            role="meter"
            aria-label={t('displayUi.capacity.title')}
            aria-valuemin={0}
            aria-valuemax={cap.capacity}
            aria-valuenow={Math.min(n, cap.capacity)}
            aria-valuetext={t('displayUi.capacity.of', { count: n, capacity: cap.capacity })}
            className="h-[3vmin] w-full overflow-hidden rounded-full bg-white/20"
          >
            <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-[1vmin] text-[clamp(1.1rem,2.4vmin,2.6rem)]">
            {t(`displayUi.capacity.level.${cap.level}`)} ({t('displayUi.capacity.of', { count: n, capacity: cap.capacity })})
          </p>
        </div>
      ) : null}
    </section>
  );
}

function Notices({ view }: { view: DisplayView }) {
  const [i, setI] = useState(0);
  const count = view.notices.length;
  useEffect(() => {
    if (count < 2) return;
    const id = setInterval(() => setI((x) => x + 1), NOTICE_MS);
    return () => clearInterval(id);
  }, [count]);
  if (count === 0) return null;
  const n = view.notices[i % count];
  if (!n) return null;
  return (
    <section className={PANEL} aria-labelledby="d-notices">
      <h2 id="d-notices" className={PANEL_TITLE}>
        {t('displayUi.notices.title')}
      </h2>
      <p className="text-[clamp(1.5rem,3.4vmin,3.6rem)] font-semibold leading-tight">{n.title}</p>
      <p className="mt-[1vmin] whitespace-pre-line text-[clamp(1.2rem,2.6vmin,2.8rem)] leading-snug">{n.text}</p>
    </section>
  );
}

function ContactlessQr({ qr }: { qr: NonNullable<DisplayView['contactlessQr']> }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(qr.url, { margin: 2, width: 600 })
      .then((d) => live && setSrc(d))
      .catch(() => live && setSrc(null));
    return () => {
      live = false;
    };
  }, [qr.url]);
  return (
    <section className={`${PANEL} flex items-center gap-[2.5vmin]`} aria-labelledby="d-qr">
      {src ? <img src={src} alt={t('displayUi.qr.alt')} className="aspect-square w-[22vmin] shrink-0 rounded-[1vmin] bg-white" /> : null}
      <div>
        <h2 id="d-qr" className="text-[clamp(1.4rem,3.2vmin,3.4rem)] font-semibold leading-tight">
          {t('displayUi.qr.title')}
        </h2>
        <p className="mt-[1vmin] text-[clamp(1.1rem,2.4vmin,2.6rem)] opacity-80">{t('displayUi.qr.description')}</p>
      </div>
    </section>
  );
}

/** The paired screen: fetches its view with the device token and keeps it fresh from the live channels. */
export function DisplayScreen({ session, onRevoked }: { session: DisplaySession; onRevoked: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: VIEW_KEY,
    queryFn: () => fetchView(session.token),
    refetchInterval: POLL_MS,
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: VIEW_KEY });
  useDisplayLive(session, {
    onRefresh: refresh,
    onAlert: (alert: SiteAlertState) => {
      // Switch at once, then let the refetch bring the rest.
      qc.setQueryData<DisplayView>(VIEW_KEY, (old) => (old ? { ...old, alert } : old));
      refresh();
    },
    onDropped: refresh,
  });

  useEffect(() => {
    if (q.error instanceof DisplayRevoked) {
      // Forget the old screen so a new pairing never flashes it.
      qc.removeQueries({ queryKey: VIEW_KEY });
      onRevoked();
    }
  }, [q.error, onRevoked, qc]);

  // After an error (the server restarting, the network down) keep what was shown and try again soon.
  const failed = q.isError && !(q.error instanceof DisplayRevoked);
  useEffect(() => {
    if (!failed) return;
    const id = setInterval(() => void qc.invalidateQueries({ queryKey: VIEW_KEY }), RETRY_MS);
    return () => clearInterval(id);
  }, [failed, qc]);

  const view = q.data;
  if (!view) {
    return (
      <main className="fixed inset-0 flex cursor-none items-center justify-center bg-slate-900 text-[clamp(1.5rem,3vmin,3rem)] text-white" role="status">
        {failed ? t('displayUi.view.offline') : t('displayUi.view.loading')}
      </main>
    );
  }
  if (view.alert.status === 'active') return <EvacuationScreen alert={view.alert} />;

  const bg = colour(view.branding.backgroundColour, '#0f172a');
  const fg = colour(view.branding.textColour, '#ffffff');
  const showHeadcount = view.content.headcount && view.headcount !== null;
  const showNotices = view.content.notices && view.notices.length > 0;
  return (
    <main className="fixed inset-0 flex cursor-none flex-col gap-[2.5vmin] overflow-hidden p-[3.5vmin]" style={{ backgroundColor: bg, color: fg }}>
      <header className="flex items-start justify-between gap-[3vmin]">
        <div className="flex min-w-0 items-center gap-[2.5vmin]">
          {view.branding.logoUrl ? <img src={view.branding.logoUrl} alt={t('displayUi.view.logoAlt')} className="max-h-[11vmin] max-w-[28vmin] object-contain" /> : null}
          <h1 className="truncate text-[clamp(1.75rem,4.6vmin,5rem)] font-bold">{view.siteName}</h1>
        </div>
        {view.content.clock ? <Clock timeZone={view.timeZone} /> : null}
      </header>
      {failed ? (
        <p role="status" className="rounded-[1.5vmin] bg-amber-300 px-[2vmin] py-[1vmin] text-[clamp(1rem,2.2vmin,2.4rem)] font-semibold text-slate-900">
          {t('displayUi.view.offline')}
        </p>
      ) : null}
      {view.message ? <p className="rounded-[2vmin] bg-white/15 px-[3vmin] py-[2vmin] text-[clamp(1.5rem,3.6vmin,4rem)] font-semibold leading-tight">{view.message}</p> : null}
      <div className="grid min-h-0 flex-1 content-start gap-[2.5vmin] landscape:grid-cols-[3fr_2fr] landscape:content-stretch">
        <div className="flex min-h-0 flex-col gap-[2.5vmin]">{view.content.welcome ? <Welcome view={view} /> : null}</div>
        <div className="flex min-h-0 flex-col gap-[2.5vmin]">
          {showHeadcount ? <Headcount view={view} /> : null}
          {showNotices ? <Notices view={view} /> : null}
          {view.contactlessQr ? <ContactlessQr qr={view.contactlessQr} /> : null}
        </div>
      </div>
    </main>
  );
}
